import { getLocalStorage } from '../../utilitys/localStorage';
import { STORAGE_TOKEN } from '../constants';
import { IErrorApi, IResponseApi } from './types';
import fileDownload from 'js-file-download';
import { ContentType, ExtensionFiles, HttpHeaders } from '../../constants/HttpHeaders';

const buildHeader = () => {
  const token = getLocalStorage<string>(STORAGE_TOKEN);
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
    'Access-Control-Allow-Origin': '*',
  };
};

const buildHeaderRange = (from: number, until: number) => {
  const token = getLocalStorage<string>(STORAGE_TOKEN);
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
    'Access-Control-Allow-Origin': '*',
    'Range': `bytes=${from}-${until}`,
  };
};

const buildHeaderWithOutContentType = () => {
  const token = getLocalStorage<string>(STORAGE_TOKEN);
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Access-Control-Allow-Origin': '*',
  };
};

export const postData = async <T, M>(
  url: string,
  data: T
): Promise<IResponseApi<M>> => {
  try {
    let result: M;

    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      headers: buildHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      return buildError(response);
    }

    result = (await response.json()) as M;
    return { hasError: false, data: result } as IResponseApi<M>;
  } catch (error) {
    return buildError(error);
  }
};

export const postFormData = async <M>(
  url: string,
  data: FormData
): Promise<IResponseApi<M>> => {
  try {
    let result: M;
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      headers: buildHeaderWithOutContentType(),
      body: data,
    });

    if (!response.ok) {
      return buildError(response);
    }

    result = (await response.json()) as M;
    return { hasError: false, data: result } as IResponseApi<M>;
  } catch (error) {
    console.log('error', error)
    return buildError(error);
  }
};

export const postDataWithOutResponse = async <T>(
  url: string,
  data: T
): Promise<IResponseApi<boolean>> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      headers: buildHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      return buildError(response);
    }

    return { hasError: false, data: true } as IResponseApi<boolean>;
  } catch (error) {
    return buildError(error);
  }
};

export const putData = async <T, M>(
  url: string,
  data: T
): Promise<IResponseApi<M>> => {
  try {
    const response = await fetch(url, {
      method: 'PUT',
      cache: 'no-cache',
      headers: buildHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      return buildError(response);
    }

    const result = (await response.json()) as M;

    return { hasError: false, data: result } as IResponseApi<M>;
  } catch (error) {
    return buildError(error);
  }
};

export const putDataWithOutResponse = async <T>(
  url: string,
  data: T
): Promise<IResponseApi<boolean>> => {
  try {
    const response = await fetch(url, {
      method: 'PUT',
      cache: 'no-cache',
      headers: buildHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      return buildError(response);
    }

    return { hasError: false, data: true } as IResponseApi<boolean>;
  } catch (error) {
    return buildError(error);
  }
};

export const getData = async <T>(url: string): Promise<IResponseApi<T>> => {
  try {
    let result: T;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      headers: buildHeader(),
    });

    if (!response.ok) {
      return buildError(response);
    }
    result = (await response.json()) as T;
    return { hasError: false, data: result } as IResponseApi<T>;
  } catch (error) {
    return buildError(error);
  }
};

export const getFile = async (url: string, data: any, fileName: string): Promise<IResponseApi<boolean>> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      headers: buildHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      return buildError(response);
    }
    const blob = (await response.blob()) as File;
    fileDownload(blob, fileName);

    return { hasError: false, data: true } as IResponseApi<boolean>;

  } catch (error) {
    return buildError(error);
  }
};

export const getHeavyFile = async (url: string, data: any, fileName: string): Promise<IResponseApi<boolean>> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      headers: buildHeader(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      return buildError(response);
    }
    const contentType = response.headers.get(HttpHeaders.ContentType);
    const contentRange = response.headers.get(HttpHeaders.ContentRange);

    if (contentType === ContentType.ApplicationZip) {
      const blob = (await response.blob()) as File;
      fileDownload(blob, `${fileName}${ExtensionFiles.Zip}`);
    }
    else if (contentType === ContentType.ApplicationOctetStream && contentRange) {
      const concatenatedBlob = await getConcatenatedBlob(contentRange, response, url, data, fileName);
      fileDownload(concatenatedBlob, `${fileName}${ExtensionFiles.Zip}`);
    }
    return { hasError: false, data: true } as IResponseApi<boolean>;
  } catch (error) {
    return buildError(error);
  }
};

export const getPart = async (url: string, data: any, fileName: string, from: number, until: number): Promise<Blob | null> => {
  const response = await fetch(url, {
    method: 'POST',
    cache: 'no-cache',
    headers: buildHeaderRange(from, until),
    body: JSON.stringify(data),
  });
  if (response.ok) {
    return await response.blob();
  }
  return null;
};

const buildError = async <T>(response: any): Promise<IResponseApi<T>> => {
  try {
    if (response.status === 400) {
      const errorModel = (await response.json()) as IErrorApi;
      return {
        hasError: true,
        error: errorModel,
        data: undefined,
      } as IResponseApi<T>;
    }
    return {
      hasError: true,
      error: undefined,
      data: undefined,
    } as IResponseApi<T>;
  } catch (error) {
    console.error(error);
    return {
      hasError: true,
      error: undefined,
      data: undefined,
    } as IResponseApi<T>;
  }
};

async function getConcatenatedBlob(contentRange: string, response: Response, url: string, data: any, fileName: string) {
  const size = parseInt(contentRange.split('/')[1]);
  const chunkSize = 20 * 1024 * 1024;
  const numChunks = Math.ceil(size / chunkSize);
  const blobs: Blob[] = [];

  const firstPart = await response.blob();
  blobs.push(firstPart);

  for (let i = 1; i < numChunks; i++) {
    const rangeFrom = chunkSize * i;
    const rangeUntil = (i === numChunks - 1) ? size : chunkSize * (i + 1) - 1;
    const nextBlob = await getPart(url, data, fileName, rangeFrom, rangeUntil);
    if (!nextBlob) break;
    blobs.push(nextBlob);
  }
  const concatenatedBlob = new Blob(blobs, { type: ContentType.ApplicationOctetStream }) as File;
  return concatenatedBlob;
}

